import React, { useMemo, useRef, useState } from 'react';
import { useLocation, useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import ReactTable from 'react-table';
import ReactLoading from 'react-loading';
import { Button, TextField, Typography, Box, Tooltip } from '@mui/material';

import Page from '../../Page';
import {
  BACK_TO_VIEW_DISPUTES,
  DISPUTES_ACTIONS,
  VIEW_DISPUTE_URL,
  BIDDER_SUCCESS_MESSAGES
} from './utils';
import { cfPath } from '../../../utilities/UrlPaths';
import { confirmSendDisputeMessage } from '../../../modules/disputes/thunks';
import MessageButton from './MessageButton';
import Alert from '../../../ui/Alert';
import TermAndConditionPopUp from './TermAndConditionPopUp';

export const ConfirmDisputes = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { state } = useLocation();
  const messageRef = useRef('');

  const { houseRef } = useSelector(store => store.house) || {};
  const { userRef } = useSelector(store => store.user);
  const sendDisputeMessageData =
    useSelector(store => store.disputes.confirmDisputeMessageData) || {};

  const [sendMessageLoading, setSendMessageLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [openTermsAndConditionModal, setOpenTermsAndConditionModal] = useState(false);

  const { success } = sendDisputeMessageData || {};
  const isMessageSend = success === 'true';
  const { disputeArray = [], selectAction = '', sendMessage = '' } = state || {};

  const TableTooltip = ({ label }) => (
    <Tooltip title={label}>
      <span className="dispute-table-data">{label}</span>
    </Tooltip>
  );

  const { buttonText, labelText, component, action } = useMemo(() => {
    switch (selectAction) {
      case DISPUTES_ACTIONS.SEND:
        return {
          action: DISPUTES_ACTIONS.SEND,
          buttonText: 'Send Message',
          labelText: 'You have chosen to send a message to these bidders',
          component: (
            <TextField
              placeholder="Please write your message here."
              inputRef={messageRef}
              defaultValue={sendMessage}
              className="buyer-message-text"
              id="outlined-textarea"
              multiline
              rows={5}
              InputLabelProps={{
                shrink: true
              }}
              variant="standard"
            />
          )
        };
      case DISPUTES_ACTIONS.CLOSE:
        return {
          action: DISPUTES_ACTIONS.CLOSE,
          buttonText: 'Close Disputes',
          labelText: 'You have chosen to Close these disputes',
          component: (
            <Box>
              <Typography variant="body2">
                Dispute must be open 4 days before it can be closed. Auction house receives a Live
                credit, Bidder added to House's No Bid List, each bidder is granted an unpaid item
                strike.
              </Typography>
              <Typography variant="body2">
                Please note — by closing these disputes you are agreeing to our{' '}
                <Button className="terms-link" onClick={() => setOpenTermsAndConditionModal(true)}>
                  dispute terms & conditions.
                </Button>
              </Typography>
            </Box>
          )
        };
      case DISPUTES_ACTIONS.CANCEL:
        return {
          action: DISPUTES_ACTIONS.CANCEL,
          buttonText: 'Cancel Disputes',
          labelText: 'You have chosen to Cancel these disputes',
          component: (
            <Typography variant="body2">
              Auction House and Buyers have resolved all issues and the buyers have paid for their
              items.
            </Typography>
          )
        };
      case DISPUTES_ACTIONS.REOPEN:
        return {
          action: DISPUTES_ACTIONS.REOPEN,
          buttonText: 'Reopen Disputes',
          labelText: 'You have chosen to reopen these disputes',
          component: <></>
        };
      default:
        return {
          buttonText: '',
          labelText: '',
          action: '',
          component: <></>
        };
    }
  }, [selectAction, disputeArray, sendMessage]);

  const column = [
    {
      Header: 'Open Date',
      id: 'openDate',
      width: 100,
      sortable: false,
      Cell: ({ original: { disputeInfo } }) => <TableTooltip label={disputeInfo.openDate} />
    },
    {
      Header: 'Bidder',
      id: 'bidder',
      width: 200,
      sortable: false,
      Cell: ({ original: { bidderInfo } }) => {
        const { firstName = '', lastName = '', channel = '', approvalID } = bidderInfo || {};
        const channelLabel = channel ? `(${channel})` : '';
        const label = `${lastName}, ${firstName} ${channelLabel}`;
        return (
          <Tooltip title={label}>
            <a
              className="dispute-table-data"
              href={`${cfPath()}/al/bidders/viewBidder.cfm?approvalID=${approvalID}`}
            >
              {label}
            </a>
          </Tooltip>
        );
      }
    },
    {
      Header: 'Lot',
      id: 'lot',
      width: 200,
      sortable: false,
      Cell: ({ original: { lotInfo } }) => {
        const { lotNumber, lotTitle, lotURL } = lotInfo || {};
        const label = `${lotNumber}: ${lotTitle}`;
        return (
          <Tooltip title={label}>
            <a className="dispute-table-data" href={lotURL} target="_blank" rel="noopener noreferrer">
              {label}
            </a>
          </Tooltip>
        );
      }
    },
    {
      Header: 'Hammer',
      id: 'hammer',
      width: 80,
      sortable: false,
      Cell: ({ original: { priceInfo } }) => {
        const { currencySymbol = '', hammer = '' } = priceInfo || {};
        const label = `${currencySymbol}${hammer}`;
        return <TableTooltip label={label} />;
      }
    },
    {
      Header: 'BP',
      id: 'bp',
      width: 80,
      sortable: false,
      Cell: ({ original: { priceInfo } }) => {
        const { currencySymbol = '', bp = '' } = priceInfo || {};
        const label = `${currencySymbol}${bp}`;
        return <TableTooltip label={label} />;
      }
    },
    {
      Header: 'Live fee',
      id: 'liveFee',
      width: 80,
      sortable: false,
      Cell: ({ original: { priceInfo } }) => {
        const { currencySymbol = '', liveFee = '' } = priceInfo || {};
        const label = `${currencySymbol}${liveFee}`;
        return <TableTooltip label={label} />;
      }
    },
    {
      Header: 'Payable',
      id: 'payable',
      width: 80,
      sortable: false,
      Cell: ({ original: { priceInfo } }) => {
        const { currencySymbol = '', payable = '' } = priceInfo || {};
        const label = `${currencySymbol}${payable}`;
        return <TableTooltip label={label} />;
      }
    },
    {
      Header: 'Reason',
      accessor: 'disputeInfo.disputeReason',
      id: 'reason',
      width: 200,
      sortable: false,
      Cell: ({ original: { disputeInfo } }) => <TableTooltip label={disputeInfo.disputeReason} />
    },
    {
      Header: 'Status',
      accessor: 'disputeInfo.disputeStat',
      id: 'status',
      sortable: false,
      Cell: ({ original: { disputeInfo } }) => <TableTooltip label={disputeInfo.disputeStat} />
    },
    {
      Header: 'Message',
      id: 'message',
      sortable: false,
      width: 80,
      Cell: ({ original }) => <MessageButton disputeData={original} />
    }
  ];

  const submitActionHandler = () => {
    const isActionSend = action === DISPUTES_ACTIONS.SEND;
    const message = isActionSend ? messageRef.current.value : '';
    if (isActionSend && !message) {
      setErrorMessage('Message is required.');
    } else {
      setErrorMessage('');
      setSendMessageLoading(true);
      const disputeIDs = disputeArray.map(({ disputeInfo }) => disputeInfo.disputeID);
      dispatch(
        confirmSendDisputeMessage(
          houseRef,
          {
            disputeIDs,
            action,
            message,
            houseUserRef: userRef
          },
          () => {
            setSendMessageLoading(false);
          }
        )
      );
    }
  };
  return (
    <Page
      header={{ title: 'View Open Disputes' }}
      navLink={{
        displayName: BACK_TO_VIEW_DISPUTES,
        href: VIEW_DISPUTE_URL
      }}
    >
      {isMessageSend ? (
        <Box>
          <Alert isOpen>
            <Typography variant="body2" fontWeight="bold">
              {BIDDER_SUCCESS_MESSAGES[action]}
            </Typography>
          </Alert>
          <Button
            className="submit-action"
            variant="contained"
            onClick={() => {
              history.push(VIEW_DISPUTE_URL);
            }}
          >
            Go Back
          </Button>
        </Box>
      ) : (
        <Box>
          <ReactTable
            columns={column}
            data={disputeArray}
            pageSize={disputeArray.length}
            minRows={0}
            style={{ width: '100%', maxHeight: '400px' }}
            showPagination={false}
            className="-highlight dispute-table"
          />
          <Box mt={3}>
            <Typography variant="body2" fontWeight="bold">
              {labelText}
            </Typography>
            {component}
            {errorMessage && (
              <Typography variant="body2" color="red" mt={1}>
                {errorMessage}
              </Typography>
            )}
          </Box>
          <Box mt={4} mb={4}>
            <Button
              disabled={sendMessageLoading}
              className="submit-action"
              variant="contained"
              onClick={submitActionHandler}
            >
              {buttonText}
              {sendMessageLoading && (
                <ReactLoading className="ml-1" type="spin" height="auto" width={15} />
              )}
            </Button>
            <Button
              sx={{ marginLeft: '20px' }}
              className="cancel-action"
              onClick={() => {
                history.push(VIEW_DISPUTE_URL);
              }}
            >
              Cancel
            </Button>
          </Box>
          <TermAndConditionPopUp
            open={openTermsAndConditionModal}
            closeModal={() => setOpenTermsAndConditionModal(false)}
          />
        </Box>
      )}
    </Page>
  );
};

export default ConfirmDisputes;
